import { msg } from "@lingui/core/macro";
import { Show } from "solid-js";
import { RotateCcw, ShieldAlert } from "@fleet/icons";
import { Card, CardContent } from "@fleet/ui";
import { Button } from "@/components/ui/button";
import { showToast } from "@/components/ui/toast";
import { api, createApiMutation } from "@/lib/api";
import { useI18n } from "@/providers/i18n-provider";
import { useAdminCapabilities } from "../../use-admin-capabilities";

/**
 * F2-C — sessions & devices tab. Per-device session data lives under the user's
 * own account; from the back-office the actionable control is revoking ALL of the
 * user's sessions (device_session rows), forcing re-authentication everywhere.
 */
export function SessionsTab(props: { userId: string }) {
  const { t } = useI18n();
  const { can } = useAdminCapabilities();

  const revokeMutation = createApiMutation(async (vars: { userId: string }) =>
    api.POST("/api/admin/revoke-sessions", { body: vars }),
  );

  return (
    <Card class="mt-4">
      <CardContent class="p-5 space-y-3">
        <div class="flex items-start gap-3">
          <ShieldAlert class="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
          <p class="text-sm text-muted-foreground">
            {t(
              msg`This user's active sessions span their devices (cookie-BFF web + native). Revoking all sessions signs them out everywhere and forces re-authentication.`,
            )}
          </p>
        </div>
        <Show when={can("users.revokeSessions")}>
          <Button
            variant="outline"
            size="sm"
            disabled={revokeMutation.isPending}
            onClick={() =>
              revokeMutation.mutate(
                { userId: props.userId },
                { onSuccess: () => showToast(t(msg`Sessions revoked`), "success") },
              )
            }
          >
            <RotateCcw class="mr-1.5 h-4 w-4" />
            {t(msg`Revoke all sessions`)}
          </Button>
        </Show>
      </CardContent>
    </Card>
  );
}
